import { BellRing } from 'lucide-react';
import { BoutonMint, BoutonSecondaire, Carte, Pastille } from '../ui/Primitives';
import { useAppareilPush, useEtatPush, type EtatAppareil } from '../../lib/usePush';

/**
 * « Notifications sur cet appareil » (TF-460) : l'apprenant y active le push
 * des rappels d'émargement sur son téléphone. Le serveur dit si le push est
 * raccordé ; le navigateur, s'il le prend en charge et si l'utilisateur l'a
 * bloqué.
 */
export function NotificationsAppareil(): JSX.Element | null {
  const { data: etat } = useEtatPush();
  const { etatAppareil, enCours, erreur, activer, desactiver } = useAppareilPush(etat);

  if (!etat) return null;

  return (
    <Carte
      titre="Notifications sur cet appareil"
      actions={etatAppareil === 'ACTIF' ? <Pastille texte="Activées" ton="mint" /> : undefined}
    >
      <div className="space-y-3 p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-codialis-blue" aria-hidden />
          <p className="min-w-0 text-sm text-codialis-gray">
            {etat.disponible ? texteEtat(etatAppareil) : TEXTE_NON_RACCORDE}
          </p>
        </div>
        {etat.disponible && etatAppareil === 'INACTIF' && (
          <BoutonMint disabled={enCours} onClick={() => void activer()}>
            {enCours ? 'Activation…' : 'Activer les notifications'}
          </BoutonMint>
        )}
        {etat.disponible && etatAppareil === 'ACTIF' && (
          <BoutonSecondaire disabled={enCours} onClick={() => void desactiver()}>
            Désactiver sur cet appareil
          </BoutonSecondaire>
        )}
        {erreur && (
          <p role="alert" className="text-xs font-medium text-red-600">
            {erreur}
          </p>
        )}
      </div>
    </Carte>
  );
}

const TEXTE_NON_RACCORDE =
  'Les notifications sur appareil ne sont pas encore ouvertes sur la plateforme : les rappels d’émargement vous parviennent par e-mail.';

function texteEtat(etat: EtatAppareil | null): string {
  switch (etat) {
    case 'ACTIF':
      return 'Les rappels d’émargement arrivent sur cet appareil, en plus de l’e-mail.';
    case 'INACTIF':
      return 'Recevez les rappels d’émargement directement sur cet appareil, en plus de l’e-mail.';
    case 'BLOQUE':
      return 'Les notifications sont bloquées pour ce site dans le navigateur : autorisez-les dans ses réglages, puis revenez ici.';
    case 'IPHONE_A_INSTALLER':
      return 'Sur iPhone, ajoutez d’abord l’application à l’écran d’accueil (bouton Partager, puis « Sur l’écran d’accueil »), ouvrez-la depuis son icône, connectez-vous et revenez ici.';
    case 'NON_PRIS_EN_CHARGE':
      return 'Ce navigateur ne reçoit pas les notifications : les rappels d’émargement vous parviennent par e-mail.';
    default:
      return 'Lecture de l’état de cet appareil…';
  }
}
